import type { ScenarioOrder } from '../../../lib/planningData';
import { hToMs } from '../../../lib/time';
import { get, isDefined } from '../../../lib/util';
import type { VesselSchedule } from '../VesselSchedule';
import { applyNewCargoAllocations } from '../Core.cargo-allocation';
import type {
  TCOrderEvents,
  TimeDataTotals,
  TimeDataInputs,
  TimeData,
} from '../TimeData';
import {
  getEventLocationId,
  getEventEndLocationId,
  getEventStartLocationId,
  isCargoEvent,
  isDeckCargo,
  isDischargeEvent,
  isLoadEvent,
  isLoadEventTimeData,
  isPortCallArrivalEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEventTimeData,
  isPortCallEventTimeData,
  isTcInEvent,
} from '../TimeData';
import type { OrderViolationLimits } from '../../ScenarioImprovementParams';
import { isFirstTcInEvent, isLastTcInEvent } from '../tc_in';
import { getVesselCapacities } from '../Vessel';

export type BestSchedule = {
  combination: OrdersToVesselCombination;
  totals: TimeDataTotals;
  inputs: TimeDataInputs;
  timeData: TimeData[];
  amount: number;
  delay: number;
  idle: number;
  cost: number;
  objectiveScore: number;
};
export type OrderPlacement = {
  order: ScenarioOrder;
  loadIndex: number;
  dischargeIndex: number;
};

export type OrdersToVesselCombination = {
  schedule: VesselSchedule;
  assignments: OrderPlacement[];
};

export type CargoAllocationOptions = {
  /** Use automatic cargo allocations? */
  allocateCargo: boolean;
  /** When using automatic allocations, are different orders of same assortment allowed to same tank */
  combineSameAssortment: boolean;
};

export const OPTION_NO_CARGO_ALLOCATIONS: CargoAllocationOptions = {
  allocateCargo: false,
  combineSameAssortment: false,
};

export type CreateAllCombinationsOptions = {
  /** With this option, never suggest combinations with added legs */
  onlyInsertInExistingPortCalls?: boolean;
  /** Can limit the number of results if we don't need all combinations */
  maxResults?: number;
  /** When set, first and last index are always allowed combinations regardless of other rules matching. */
  alwaysAllowMinimumAndMaximumIndex?: boolean;
  minimumIndex?: number;
  maximumIndex?: number;
  firstAllowedTime?: number;
  violationLimitsMs?: OrderViolationLimits | null;
  /** Normally we'll want to load in the order of laycans (when not overlapping), however even
   * if our layday/rta is earlier than that of an event we compare to, we'll allow a violation of only this much.
   */
  maxAllowedLaydayRtaTooEarly?: number;
  /** Multiplier to how much margin we allow for loading over capacity */
  cargoMargin?: number;
  allocationOptions: CargoAllocationOptions;
} & (
  | {
      onlyInsertInBallast?: false;
      assignedOrderIds?: Set<number>;
    }
  | {
      onlyInsertInBallast: true;
      /** Orders have already been assigned together with the ones being processed now */
      assignedOrderIds: Set<number>;
    }
);

export const CreateAllCombinations = (
  events: VesselSchedule,
  orderEvents: TCOrderEvents,
  currentTimedata: TimeData[],
  {
    onlyInsertInBallast = false,
    assignedOrderIds,
    onlyInsertInExistingPortCalls = false,
    maxResults = Infinity,
    alwaysAllowMinimumAndMaximumIndex = false,
    minimumIndex = -Infinity,
    maximumIndex = Infinity,
    firstAllowedTime = +new Date(),
    violationLimitsMs,
    maxAllowedLaydayRtaTooEarly = hToMs(24 * 3),
    cargoMargin = 1.05,
    allocationOptions,
  }: CreateAllCombinationsOptions,
): OrdersToVesselCombination[] => {
  // Only inserting in ballast affects only the first order of the bunch strictly, the rest are allowed to overlap
  // whatever was already assigned together with them.
  if (onlyInsertInBallast && assignedOrderIds?.size) {
    minimumIndex = Math.max(
      minimumIndex ?? -Infinity,
      events.findIndex(
        (e) => isCargoEvent(e) && assignedOrderIds.has(e.order.id),
      ),
    );
    // Reverse to the beginning of this port call, unless we would overlap with another shipment's discharge
    while (
      minimumIndex > 0 &&
      !isDischargeEvent(events[minimumIndex - 1]) &&
      getEventStartLocationId(events[minimumIndex - 1]) ===
        getEventStartLocationId(events[minimumIndex])
    ) {
      minimumIndex--;
    }

    maximumIndex = Math.min(
      maximumIndex ?? Infinity,
      events.findLastIndex(
        (e) => isCargoEvent(e) && assignedOrderIds.has(e.order.id),
      ) + 1,
    );
    // Continue to the end of this port call, unless we would overlap with another shipment's load
    while (
      maximumIndex < events.length &&
      !isLoadEvent(events[maximumIndex]) &&
      getEventStartLocationId(events[maximumIndex - 1]) ===
        getEventStartLocationId(events[maximumIndex])
    ) {
      maximumIndex++;
    }

    onlyInsertInBallast = false;
  }

  const schedules: OrdersToVesselCombination[] = [];
  const { load: originalLoad, discharge: originalDischarge } = orderEvents;

  // Make copies of events so that mutating them in combinations is ok
  const createEvents = () => {
    const entry = { ...originalLoad.entry };
    return [
      {
        ...originalLoad,
        entry,
      },
      {
        ...originalDischarge,
        entry,
      },
    ];
  };

  let [load, discharge] = createEvents();

  const cargoRange = load.order.cargoRanges;
  const minMass = cargoRange.mass.min;
  const minVolume = cargoRange.volume.min;
  const isDeckOrder = isDeckCargo(load.order.orderDetailsByOrderId.nodes);
  const maxLoadIndex = Math.min(events.length, maximumIndex);
  const maxDischargeIndex = maxLoadIndex + 1;

  const vessel = orderEvents.load.vessel;
  const vesselCapacities = getVesselCapacities(vessel);
  const vesselMassCapacity = vesselCapacities.massCapacity;
  const vesselVolumeCapacity = vesselCapacities.volumeCapacity;
  const vesselDeckVolumeCapacity = vesselCapacities.deckVolumeCapacity ?? 0;

  const firstFutureIndex = currentTimedata.findIndex((e, i, arr) => {
    const next = arr.at(i + 1);
    /** Do not consider arrival for timing, because it typically follows the end time of the previous event */
    const compareTime =
      isPortCallArrivalEventTimeData(e) && next ? next.start : e.start;
    return compareTime >= firstAllowedTime;
  });
  const loadLocationId = getEventLocationId(load);
  const dischargeLocationId = getEventLocationId(discharge);

  const firstIndex = Math.max(
    minimumIndex,
    firstFutureIndex === -1 ? maxLoadIndex : firstFutureIndex,
  );

  /** The last event before the first position where we are allowed to start trying placement.
   * We use this for a special case to allow inserting AFTER the departure even if it's the same location,
   * because we are not allowed to go back one index to insert normally in the correct place right before departure.
   */
  const lastFixedEvent = get(currentTimedata, firstIndex - 1);
  const lastFixedEventLocation =
    lastFixedEvent && getEventEndLocationId(lastFixedEvent.event);
  const lastFixedEventDepartureIsSameLocationAsLoad =
    !!lastFixedEvent &&
    lastFixedEventLocation === loadLocationId &&
    isPortCallDepartureEventTimeData(lastFixedEvent);

  const earliestLoading = load.order.loadingWindow.start;
  const latestLoading = load.order.loadingWindow.end;
  const earliestDischarging = load.order.dischargingWindow.start;
  const latestDischarging = load.order.dischargingWindow.end;

  let firstEventInLoadPortCall: TimeData | undefined = undefined;

  for (let loadIndex = firstIndex; loadIndex <= maxLoadIndex; loadIndex++) {
    const beforeLoad: TimeData | undefined = currentTimedata[loadIndex - 1];

    const loadConstraint =
      firstEventInLoadPortCall?.arriveInPort ?? beforeLoad?.end;
    if (
      isDefined(violationLimitsMs?.load.late) &&
      latestLoading &&
      loadConstraint &&
      loadConstraint > latestLoading + violationLimitsMs.load.late
    ) {
      break;
    }

    // Check capacity constraints. When beforeLoad is undefined (index 0),
    // use vessel's initial capacity.
    const availableMassCapacity = beforeLoad
      ? beforeLoad.availableMassCapacity
      : vesselMassCapacity;
    const availableVolumeCapacity = isDeckOrder
      ? beforeLoad
        ? beforeLoad.availableDeckVolumeCapacity
        : vesselDeckVolumeCapacity
      : beforeLoad
        ? beforeLoad.availableVolumeCapacity
        : vesselVolumeCapacity;

    if (
      availableMassCapacity * cargoMargin < minMass ||
      availableVolumeCapacity * cargoMargin < minVolume ||
      (onlyInsertInBallast && beforeLoad && beforeLoad.ordersOnBoard > 0)
    ) {
      continue;
    }
    const afterLoad = get(currentTimedata, loadIndex);
    const beforeLoadEvent = beforeLoad?.event;
    const afterLoadEvent = afterLoad?.event;

    // We cannot load anything before the first TC In event (TC In event with no redelivery) which is always assumed to
    // be at the first index of a vessel schedule
    if (
      afterLoadEvent &&
      isTcInEvent(afterLoadEvent) &&
      isFirstTcInEvent(afterLoadEvent)
    ) {
      continue;
    }

    // We cannot load anything after the last TC In event
    if (
      beforeLoadEvent &&
      isTcInEvent(beforeLoadEvent) &&
      isLastTcInEvent(beforeLoadEvent)
    ) {
      break;
    }

    /** The last legal time placement for the event that would be right after our load if we added load in this position */
    const afterLoadLastAllowed =
      afterLoadEvent &&
      (isLoadEvent(afterLoadEvent)
        ? afterLoadEvent.order.loadingWindow.end
        : isDischargeEvent(afterLoadEvent)
          ? afterLoadEvent.order.dischargingWindow.end
          : undefined);

    // Existing cargo needs to be handled before we have layday on the current order, we can't load this early
    if (
      earliestLoading &&
      afterLoadLastAllowed &&
      earliestLoading > afterLoadLastAllowed + maxAllowedLaydayRtaTooEarly
    ) {
      continue;
    }

    const locationIdBeforeLoad =
      beforeLoadEvent && getEventEndLocationId(beforeLoadEvent);
    const locationIdAfterLoad =
      afterLoadEvent && getEventStartLocationId(afterLoadEvent);

    if (
      loadIndex === firstFutureIndex &&
      locationIdBeforeLoad === loadLocationId &&
      beforeLoad &&
      isPortCallDepartureEventTimeData(beforeLoad)
    ) {
      // Edge case: we already left the same location where we would load. Adding to this index would have us slip the event inside the previous port call, violating the minimum index.
      continue;
    }

    if (
      onlyInsertInExistingPortCalls &&
      locationIdBeforeLoad !== loadLocationId &&
      locationIdAfterLoad !== loadLocationId
    ) {
      continue;
    }

    const loadIsBeforeMatchingDeparture =
      !!afterLoad &&
      isPortCallDepartureEventTimeData(afterLoad) &&
      getEventStartLocationId(afterLoad.event) === loadLocationId;

    const loadIsLastEventInMatchingPortCall =
      // When events array does include port call events
      loadIsBeforeMatchingDeparture ||
      // When events array doesn't include port call events
      (!!beforeLoad &&
        locationIdBeforeLoad === loadLocationId &&
        locationIdAfterLoad !== loadLocationId &&
        !isPortCallEventTimeData(beforeLoad));

    const loadIsBetweenLocations =
      (locationIdBeforeLoad !== locationIdAfterLoad &&
        locationIdBeforeLoad !== loadLocationId &&
        locationIdAfterLoad !== loadLocationId) ||
      // Practically only relevant for an empty timeline
      (locationIdBeforeLoad === undefined && locationIdAfterLoad === undefined);

    const isValidBecauseFirstOrLastIndex =
      alwaysAllowMinimumAndMaximumIndex &&
      (loadIndex == minimumIndex || loadIndex === maximumIndex);
    const isValidBecauseGoodPlacement =
      loadIsLastEventInMatchingPortCall ||
      loadIsBetweenLocations ||
      (lastFixedEventDepartureIsSameLocationAsLoad && loadIndex === firstIndex);
    /** New loads always to be the last load in a port call */
    const canLoadLaterInThisPC =
      loadIndex < maximumIndex &&
      locationIdAfterLoad === loadLocationId &&
      afterLoad &&
      isLoadEventTimeData(afterLoad);

    firstEventInLoadPortCall = canLoadLaterInThisPC
      ? (firstEventInLoadPortCall ?? beforeLoad)
      : undefined;

    if (
      (!isValidBecauseFirstOrLastIndex && !isValidBecauseGoodPlacement) ||
      canLoadLaterInThisPC
    ) {
      continue;
    }
    const maxDischarge = onlyInsertInBallast
      ? loadIndex + 1
      : maxDischargeIndex;

    for (
      let dischargeIndex = loadIndex,
        shouldBreak = false,
        loadPortDeparted = false;
      dischargeIndex <= maxDischarge && !shouldBreak;
      dischargeIndex++
    ) {
      const beforeDischargeIndex = dischargeIndex - 1;
      const beforeDischarge = get(currentTimedata, beforeDischargeIndex);

      if (
        isDefined(violationLimitsMs?.discharge.late) &&
        beforeDischarge &&
        latestDischarging &&
        beforeDischarge.end >
          latestDischarging + violationLimitsMs.discharge.late
      ) {
        break;
      }

      const afterDischarge = get(currentTimedata, dischargeIndex);
      const beforeDischargeEvent =
        dischargeIndex === loadIndex ? load : beforeDischarge?.event;
      const afterDischargeEvent = afterDischarge?.event;
      const locationIdBeforeDischarge =
        beforeDischargeEvent && getEventLocationId(beforeDischargeEvent);
      const locationIdAfterDischarge =
        afterDischargeEvent && getEventLocationId(afterDischargeEvent);

      // If the current event at this index is a TC In event it means that this index is the last allowed position for
      // the current TC In period
      if (afterDischargeEvent && isTcInEvent(afterDischargeEvent)) {
        shouldBreak = true;
      }

      // Capacity must be checked before the location filter below, otherwise
      // intermediate positions where cargo exceeds capacity are skipped without
      // detection when onlyInsertInExistingPortCalls is on.
      if (dischargeIndex > loadIndex && beforeDischarge) {
        const availableVolumeCapacityDischarge = isDeckOrder
          ? beforeDischarge.availableDeckVolumeCapacity
          : beforeDischarge.availableVolumeCapacity;
        if (
          beforeDischarge.availableMassCapacity * cargoMargin < minMass ||
          availableVolumeCapacityDischarge * cargoMargin < minVolume
        ) {
          break;
        }
      }

      if (
        onlyInsertInExistingPortCalls &&
        locationIdBeforeDischarge !== dischargeLocationId &&
        locationIdAfterDischarge !== dischargeLocationId
      ) {
        continue;
      }

      /** The last legal time placement for the event that would be right after our discharge if we added discharge in this position */
      const afterDischargeLastAllowed =
        afterDischargeEvent &&
        (isLoadEvent(afterDischargeEvent)
          ? afterDischargeEvent.order.loadingWindow.end
          : isDischargeEvent(afterDischargeEvent)
            ? afterDischargeEvent.order.dischargingWindow.end
            : undefined);

      // Existing cargo needs to be handled before we have rta on the current order, we can't discharge this early
      if (
        earliestDischarging &&
        afterDischargeLastAllowed &&
        earliestDischarging >
          afterDischargeLastAllowed + maxAllowedLaydayRtaTooEarly
      ) {
        continue;
      }

      if (locationIdBeforeDischarge !== loadLocationId) {
        loadPortDeparted = true;
      }
      // If we enter the same port as load again before discharging we have made a pointless trip, no reason to continue
      if (loadPortDeparted && locationIdBeforeDischarge === loadLocationId) {
        break;
      }

      const dischargeIsAfterMatchingBerthing =
        !!beforeDischarge &&
        getEventEndLocationId(beforeDischarge.event) === dischargeLocationId &&
        isPortCallBerthingEventTimeData(beforeDischarge);

      const dischargeIsFirstEventInMatchingPortCall =
        // When events array does include port call events
        dischargeIsAfterMatchingBerthing ||
        // When events array doesn't include port call events
        (!!afterDischarge &&
          locationIdBeforeDischarge !== dischargeLocationId &&
          locationIdAfterDischarge === dischargeLocationId &&
          !isPortCallEventTimeData(afterDischarge));

      if (dischargeIsFirstEventInMatchingPortCall) {
        // If we already find and try the same location, no later position can be better so we will break after trying this position
        shouldBreak = true;
      }

      const dischargeIsBetweenLocations =
        locationIdBeforeDischarge !== locationIdAfterDischarge &&
        locationIdBeforeDischarge !== dischargeLocationId &&
        locationIdAfterDischarge !== dischargeLocationId;
      const isValidBecauseFirstOrLastIndex =
        alwaysAllowMinimumAndMaximumIndex && dischargeIndex === maximumIndex;
      const isValidBecauseGoodPlacement =
        dischargeIsFirstEventInMatchingPortCall || dischargeIsBetweenLocations;
      if (!isValidBecauseFirstOrLastIndex && !isValidBecauseGoodPlacement) {
        continue;
      }
      let schedule = events.slice();

      /** If load would be placed into the past, delay to first allowed time */
      if (
        beforeLoad &&
        beforeLoad.end < firstAllowedTime &&
        (!load.delayUntil || load.delayUntil < firstAllowedTime)
      ) {
        load.delayUntil = firstAllowedTime;
      }

      schedule.splice(loadIndex, 0, load);
      schedule.splice(dischargeIndex + 1, 0, discharge);

      if (allocationOptions.allocateCargo) {
        schedule = applyNewCargoAllocations(
          schedule,
          [load.order.id],
          allocationOptions,
        );
      }

      schedules.push({
        schedule,
        assignments: [
          {
            order: load.order,
            loadIndex,
            dischargeIndex: dischargeIndex + 1,
          },
        ],
      });

      if (schedules.length >= maxResults) {
        return schedules;
      }

      [load, discharge] = createEvents();
    }
  }

  return schedules;
};
